import { useMutation } from '@apollo/client';
import { useCustomToast } from 'shared/hooks/useCustomToast';
import React, { useMemo } from 'react';
import { UPDATE_WORKFLOW_EVENT_SETTING } from 'shared/queries/workflows';
import { WorkflowEvent, WorkflowSendingHours } from 'shared/types/graphql';
import styled from 'styled-components/macro';
import { Select, SelectOptionProps, Switch, Center } from '@televet/kibble-ui';
import AutomationGrid, { IGridContentProps } from './AutomationGrid';
import { WorkflowTemplate } from './AutomationTable';

type IAutomationLineProps = {
  workflowEventSetting: WorkflowTemplate;
  isAutomationHoursSet: boolean;
  hasPermission?: boolean;
  index: number;
};

const workflowEvents: { [key: string]: string } = {
  [WorkflowEvent.AppointmentFollowup]: 'Appointment Follow up',
  [WorkflowEvent.AppointmentPimsSync]: 'Appointment PIMS Sync',
  [WorkflowEvent.AppointmentClientArrival]: 'Client Arrival',
  [WorkflowEvent.AppointmentConfirmReminder]: 'Confirm Reminder',
  [WorkflowEvent.WidgetRequestFormSubmitted]: 'Website Widget Request',
  [WorkflowEvent.AppointmentReminder]: 'Reminder',
  [WorkflowEvent.AppointmentScheduled]: 'Schedule',
  [WorkflowEvent.ServiceReminderFormSubmitted]: 'Service Reminder Form Submitted',
  [WorkflowEvent.CallDeflectionFormSubmitted]: 'Call Concierge Form Submitted',
};

const AutomationLine = ({
  workflowEventSetting,
  isAutomationHoursSet,
  index,
  hasPermission,
}: IAutomationLineProps): JSX.Element => {
  const { toastMessagePop } = useCustomToast();

  const isSendingHoursVisible = useMemo(() => {
    const event = workflowEventSetting.event as WorkflowEvent;
    if (!event) return false;
    return ![WorkflowEvent.ServiceReminderFormSubmitted, WorkflowEvent.WidgetRequestFormSubmitted].includes(event);
  }, [workflowEventSetting.event]);

  const [updateWorkflowEventSetting] = useMutation(UPDATE_WORKFLOW_EVENT_SETTING);

  const changeSendingHours = (selectedItem: SelectOptionProps): void => {
    updateWorkflowEventSetting({
      variables: { where: { id: workflowEventSetting.id }, data: { sendingHours: selectedItem.value } },
    });
    toastMessagePop();
  };

  const toggleActive = (event: React.ChangeEvent<HTMLInputElement>): void => {
    updateWorkflowEventSetting({
      variables: { where: { id: workflowEventSetting.id }, data: { isPublished: event.target.checked } },
    });
    toastMessagePop();
  };

  const listOption = [
    {
      isSelected: false,
      label: 'Clinic Hours',
      value: WorkflowSendingHours.BusinessHours,
    },
    {
      isSelected: false,
      label: '24 Hours a Day',
      value: WorkflowSendingHours.All24Hours,
    },
  ];
  const gridContent: IGridContentProps = {
    name: workflowEventSetting.name,
    sendingHours: isSendingHoursVisible && (
      <StyledBox haspermission={hasPermission || false}>
        <Select
          isDisabled={!hasPermission}
          labelStyle={{
            fontWeight: 'semibold',
          }}
          listProps={{
            isGrouped: false,
            isSearchable: false,
            onSelect: (selectedItem: SelectOptionProps): void => changeSendingHours(selectedItem),
          }}
          options={[
            ...listOption,
            ...(isAutomationHoursSet
              ? [
                  {
                    isSelected: false,
                    label: 'Automation Hours',
                    value: WorkflowSendingHours.AutomationHours,
                  },
                ]
              : []),
          ]}
          size="sm"
          w="180px"
          value={workflowEventSetting.sendingHours}
        />
      </StyledBox>
    ),
    event: workflowEvents[workflowEventSetting.event],
    clientOrPet: workflowEventSetting.visitType,
    active: (
      <Center h="100%">
        <Switch
          isDisabled={!hasPermission}
          isChecked={workflowEventSetting.isPublished}
          onChange={toggleActive}
          size="md"
        />
      </Center>
    ),
  };

  return (
    <AutomationGrid
      textSize="sm"
      automationGridContent={gridContent}
      background={index % 2 !== 0 ? 'background.default' : 'transparent'}
    />
  );
};

export default AutomationLine;

const StyledBox = styled.div<{ haspermission: boolean }>`
  & .chakra-select__icon {
    display: ${({ haspermission }): string => (haspermission ? 'inline-flex' : 'none !important')};
  }
`;
